import { useMemo } from "react";
import { useLocation, useNavigate, type Location, type To } from "react-router";

type HistoryTarget = string | { pathname?: string; search?: string; hash?: string; state?: unknown };

function toNavigateTarget(to: HistoryTarget): { to: To; state?: unknown } {
	if (typeof to === "string") {
		return { to };
	}
	return {
		to: { pathname: to.pathname, search: to.search, hash: to.hash },
		state: to.state,
	};
}

interface AppHistory {
	push: (to: HistoryTarget, state?: unknown) => void;
	replace: (to: HistoryTarget, state?: unknown) => void;
	goBack: () => void;
	go: (delta: number) => void;
	location: Location;
}

/**
 * v5 `useHistory` shape on top of `useNavigate`.
 * `push(path, state)` and `push({ pathname, search, state })` both keep location state.
 */
export function useHistory(): AppHistory {
	const navigate = useNavigate();
	const location = useLocation();

	return useMemo(
		() => ({
			push(to: HistoryTarget, state?: unknown) {
				const target = toNavigateTarget(to);
				navigate(target.to, { state: state ?? target.state });
			},
			replace(to: HistoryTarget, state?: unknown) {
				const target = toNavigateTarget(to);
				navigate(target.to, { replace: true, state: state ?? target.state });
			},
			goBack() {
				navigate(-1);
			},
			go(delta: number) {
				navigate(delta);
			},
			location,
		}),
		[location, navigate]
	);
}
